import type { TrainingProfile } from '../schema/profile';

const t = (cs: string, en: string) => ({ 'cs-CZ': cs, 'en-US': en });

/**
 * Chladiva a F-plyny - V1 DRAFT - authored for structural completeness; NOT a
 * substitute for sign-off by a Czech osoba odborně způsobilá v prevenci rizik.
 * Legal refs: z. 73/2012 Sb. (o látkách, které poškozují ozonovou vrstvu, a o
 * fluorovaných skleníkových plynech) + Reg. (EU) 2024/573 (F-gases). THEORY
 * ONLY - any intervention on a refrigerant circuit (installation, service,
 * leak checks, recovery) requires a personal certificate obtained by a
 * theory + practical exam at an accredited body, with refresher/update at
 * least every 7 years (the seed carries the FGAS_CERT qualification ref →
 * theory-only banner in the player; the hazard is a hard legal gate).
 * Bilingual (cs-CZ + en-US). Three chapters (kdy smíš na okruh | rizika
 * chladiv | zákaz vypouštění a odsávání) + a graded quiz with one question
 * per kind. Answer keys live here (repo-privileged) and in the DB; stripped
 * from the client payload.
 */
export const fGasesProfile: TrainingProfile = {
  slug: 'f-gases',
  // Top-level title is a PLAIN STRING - seeded verbatim into the varchar column.
  title: 'Chladiva a F-plyny',
  passThreshold: 80,
  content: {
    schemaVersion: 1,
    chapters: [
      {
        id: 'ch-certifikace',
        title: t('Kdy smíš na chladivový okruh', 'When you may touch a refrigerant circuit'),
        minDwellSeconds: 20,
        blocks: [
          {
            id: 'h-certifikace',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Bez certifikátu okruh neotvírej', 'No certificate, no opening the circuit'),
          },
          {
            id: 'p-certifikace-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Klimatizace, tepelná čerpadla a chladicí technika obsahují **fluorované skleníkové plyny (F-plyny)**. Jakýkoli **zásah do chladivového okruhu** - instalace, servis, kontrola těsnosti, odsátí chladiva - smí provádět jen osoba s **osobním certifikátem** od akreditovaného hodnotícího a certifikačního subjektu. Necertifikovaná práce na okruhu je **protiprávní**, ne jen rizikovější.',
              'Air-conditioning units, heat pumps and refrigeration equipment contain **fluorinated greenhouse gases (F-gases)**. Any **intervention on the refrigerant circuit** - installation, service, leak checks, refrigerant recovery - may only be performed by a person holding a **personal certificate** from an accredited evaluation and certification body. Uncertified work on the circuit is **unlawful**, not merely riskier.',
            ),
          },
          {
            id: 'c-certifikace-pravo',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'danger',
            markdown: t(
              'Právní základ: **z. 73/2012 Sb.** + **nařízení (EU) 2024/573**. Certifikát se získává **teoretickou a praktickou zkouškou** a znalosti je nutné obnovovat **nejméně jednou za 7 let**. Tento kurz je **teorie** - certifikát nenahrazuje.',
              'Legal basis: **Act 73/2012 Coll.** + **Regulation (EU) 2024/573**. The certificate is obtained by a **theory and practical exam**, and knowledge must be refreshed **at least every 7 years**. This course is **theory** - it does not replace the certificate.',
            ),
          },
          {
            id: 'p-certifikace-2',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Co **smíš bez certifikátu**: práce, které okruh neotevírají - čištění filtrů a výměníků, kontrola odvodu kondenzátu, elektrické zapojení mimo okruh, montáž držáků. Jakmile má přijít na řadu **potrubí s chladivem, servisní ventily nebo plnění**, práci předej certifikovanému technikovi.',
              'What you **may do without a certificate**: work that does not open the circuit - cleaning filters and heat exchangers, checking condensate drainage, electrical wiring outside the circuit, mounting brackets. The moment **refrigerant piping, service valves or charging** comes up, hand the job to a certified technician.',
            ),
          },
          {
            id: 'cl-certifikace',
            blockType: 'checklist',
            schemaVersion: 1,
            items: [
              t(
                'Zásah do okruhu = jen s osobním certifikátem',
                'Circuit intervention = personal certificate only',
              ),
              t(
                'Čištění filtrů a kondenzát = bez certifikátu OK',
                'Filter cleaning and condensate = OK without certificate',
              ),
              t(
                'Kontrola těsnosti a odsátí = certifikovaná práce',
                'Leak checks and recovery = certified work',
              ),
              t(
                'Obnova znalostí nejméně 1× za 7 let',
                'Knowledge refresh at least once every 7 years',
              ),
            ],
          },
        ],
      },
      {
        id: 'ch-rizika',
        title: t('Rizika chladiv', 'Refrigerant hazards'),
        minDwellSeconds: 20,
        requiredWidgetIds: ['kc-rizika'],
        blocks: [
          {
            id: 'h-rizika',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Těžší než vzduch, studenější než mráz', 'Heavier than air, colder than frost'),
          },
          {
            id: 'p-rizika-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Unikající chladivo je **těžší než vzduch** - klesá k podlaze a v suterénu, šachtě nebo malé strojovně **vytěsní kyslík** bez zápachu a varování. Kapalné chladivo při úniku okamžitě chladne hluboko pod bod mrazu a způsobí **omrzliny**. Novější chladiva třídy **A2L (např. R32) jsou mírně hořlavá** - u úniku nikdy neškrtej a nesvařuj.',
              'Leaking refrigerant is **heavier than air** - it sinks to the floor and in a basement, shaft or small plant room it **displaces oxygen** with no smell and no warning. Liquid refrigerant flashing off cools far below freezing and causes **frostbite**. Newer **A2L-class refrigerants (e.g. R32) are mildly flammable** - never strike a flame or weld near a leak.',
            ),
          },
          {
            id: 'anim-rizika',
            blockType: 'animation',
            schemaVersion: 1,
            scene: 'refrigerantLeak',
            steps: [
              {
                label: t('Únik u podlahy', 'Leak pools at the floor'),
                say: t(
                  'Bez zápachu, těžší než vzduch - hromadí se u podlahy.',
                  'Odourless, heavier than air - pools at the floor.',
                ),
              },
              {
                label: t('Certifikované odsátí', 'Certified recovery'),
                say: t(
                  'Odsátí do servisní lahve jen certifikovanou osobou.',
                  'Recovery into a service cylinder by a certified person only.',
                ),
              },
            ],
            caption: t(
              'Únik ze splitové jednotky klesá k podlaze a vytěsňuje kyslík. Plamen v blízkosti úniku = riziko vznícení a toxických zplodin.',
              'A leak from a split unit sinks to the floor and displaces oxygen. A flame near the leak = risk of ignition and toxic fumes.',
            ),
          },
          {
            id: 'p-rizika-2',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Při zahřátí (pájení, svařování, požár) se chladiva rozkládají na **toxické zplodiny včetně fluorovodíku** - leptají dýchací cesty. Proto se před pájením okruh vždy odsává a proplachuje. Ucítíš-li nasládlý chemický zápach nebo syčení, **větrej, nezdržuj se u podlahy a přivolej certifikovaného technika**.',
              'When heated (brazing, welding, fire), refrigerants decompose into **toxic products including hydrogen fluoride** - corrosive to the airways. That is why the circuit is always recovered and purged before brazing. If you notice a sweetish chemical smell or hissing, **ventilate, stay off floor level and call a certified technician**.',
            ),
          },
          {
            id: 'kc-rizika',
            blockType: 'knowledgeCheck',
            schemaVersion: 1,
            prompt: t(
              'Proč je únik chladiva v suterénu zvlášť nebezpečný?',
              'Why is a refrigerant leak in a basement especially dangerous?',
            ),
            choices: [
              {
                id: 'kc-a',
                label: t(
                  'Chladivo stoupá ke stropu a uniká ven',
                  'Refrigerant rises to the ceiling and escapes',
                ),
              },
              {
                id: 'kc-b',
                label: t(
                  'Je těžší než vzduch - u podlahy vytěsní kyslík',
                  'It is heavier than air - it displaces oxygen at floor level',
                ),
              },
              {
                id: 'kc-c',
                label: t('Silně páchne a dráždí oči', 'It smells strongly and irritates the eyes'),
              },
            ],
            correctChoiceId: 'kc-b',
            explanation: t(
              'F-plyny jsou bez zápachu a těžší než vzduch. V nevětraném suterénu se hromadí u podlahy a dusí bez varování.',
              'F-gases are odourless and heavier than air. In an unventilated basement they pool at floor level and asphyxiate without warning.',
            ),
          },
        ],
      },
      {
        id: 'ch-vypousteni',
        title: t('Zákaz vypouštění a odsávání', 'The venting ban and recovery'),
        minDwellSeconds: 20,
        requiredWidgetIds: ['ord-odsavani'],
        blocks: [
          {
            id: 'h-vypousteni',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Do vzduchu nikdy', 'Never into the air'),
          },
          {
            id: 'p-vypousteni-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              '**Úmyslné vypuštění F-plynu do ovzduší je zakázané** - i „jen zbytek" z odpojované jednotky. Chladivo se vždy **odsává certifikovanou odsávačkou do servisní lahve** a předává k recyklaci nebo likvidaci. Servisní lahev je **tlaková nádoba**: plní se nejvýše na stanovený limit, přepravuje se zajištěná, s našroubovanou ochrannou čepičkou ventilu.',
              '**Deliberately venting F-gas to the atmosphere is prohibited** - even "just the remainder" from a unit being disconnected. Refrigerant is always **recovered with a certified recovery machine into a service cylinder** and handed over for recycling or destruction. The service cylinder is a **pressure vessel**: fill it only to the set limit, transport it secured, with the valve protection cap screwed on.',
            ),
          },
          {
            id: 'c-vypousteni-evidence',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'warning',
            markdown: t(
              'O odsátí, plnění a kontrolách těsnosti se vede **evidence (záznamy o zařízení)**. Chybějící záznam je při kontrole ČIŽP stejný problém jako únik sám.',
              'Recovery, charging and leak checks must be **logged (equipment records)**. A missing record is as much of a problem in a ČIŽP inspection as the leak itself.',
            ),
          },
          {
            id: 'ord-odsavani',
            blockType: 'ordering',
            schemaVersion: 1,
            prompt: t(
              'Seřaď správný postup před demontáží jednotky.',
              'Order the correct steps before dismantling a unit.',
            ),
            items: [
              {
                id: 'oo-1',
                label: t(
                  'Ověření certifikátu a příprava odsávačky',
                  'Verify the certificate and set up the recovery machine',
                ),
              },
              {
                id: 'oo-2',
                label: t(
                  'Odsátí chladiva do servisní lahve',
                  'Recover the refrigerant into a service cylinder',
                ),
              },
              {
                id: 'oo-3',
                label: t(
                  'Zápis množství do evidence zařízení',
                  'Log the quantity in the equipment records',
                ),
              },
              {
                id: 'oo-4',
                label: t(
                  'Demontáž a předání lahve k recyklaci',
                  'Dismantle and hand the cylinder over for recycling',
                ),
              },
            ],
          },
          {
            id: 'k-vypousteni',
            blockType: 'keyTakeaway',
            schemaVersion: 1,
            markdown: t(
              '**Okruh otvírá jen certifikovaný technik.** Chladivo nikdy do vzduchu - vždy odsát, zaevidovat, předat. U úniku: větrat, žádný plamen, pryč od podlahy.',
              '**Only a certified technician opens the circuit.** Refrigerant never goes into the air - always recover, log, hand over. At a leak: ventilate, no flames, stay off floor level.',
            ),
          },
        ],
      },
    ],
  },
  questions: [
    {
      id: 'q-certifikace-single',
      kind: 'single',
      chapterId: 'ch-certifikace',
      prompt: t(
        'Kdo smí odsát chladivo z tepelného čerpadla?',
        'Who may recover refrigerant from a heat pump?',
      ),
      choices: [
        {
          id: 'a',
          label: t(
            'Jen osoba s osobním certifikátem pro F-plyny',
            'Only a person with a personal F-gas certificate',
          ),
        },
        {
          id: 'b',
          label: t('Každý instalatér s tímto e-kurzem', 'Any plumber who completed this e-course'),
        },
        {
          id: 'c',
          label: t(
            'Kdokoli pod dohledem majitele zařízení',
            'Anyone supervised by the equipment owner',
          ),
        },
      ],
      correct: 'a',
    },
    {
      id: 'q-vypousteni-tf',
      kind: 'truefalse',
      chapterId: 'ch-vypousteni',
      prompt: t(
        'Malý zbytek chladiva z odpojované klimatizace je v pořádku vypustit do vzduchu.',
        'It is fine to vent a small remainder of refrigerant from an AC unit being disconnected.',
      ),
      correct: false,
    },
    {
      id: 'q-rizika-multi',
      kind: 'multi',
      chapterId: 'ch-rizika',
      prompt: t(
        'Jaká rizika má unikající chladivo? (vyber všechny správné)',
        'What hazards does leaking refrigerant carry? (select all that apply)',
      ),
      choices: [
        { id: 'a', label: t('Vytěsnění kyslíku u podlahy', 'Oxygen displacement at floor level') },
        {
          id: 'b',
          label: t('Omrzliny od kapalného chladiva', 'Frostbite from liquid refrigerant'),
        },
        { id: 'c', label: t('Silný varovný zápach', 'A strong warning smell') },
        {
          id: 'd',
          label: t(
            'Toxické zplodiny při kontaktu s plamenem',
            'Toxic decomposition products on contact with a flame',
          ),
        },
      ],
      correct: ['a', 'b', 'd'],
    },
    {
      id: 'q-obnova-numeric',
      kind: 'numeric',
      chapterId: 'ch-certifikace',
      prompt: t(
        'Nejméně jednou za kolik let musí certifikovaná osoba obnovit znalosti?',
        'At least once every how many years must a certified person refresh their knowledge?',
      ),
      correct: 7,
      tolerance: 0,
    },
    {
      id: 'q-odsavani-order',
      kind: 'ordering',
      chapterId: 'ch-vypousteni',
      prompt: t(
        'Seřaď postup před demontáží jednotky.',
        'Order the steps before dismantling a unit.',
      ),
      choices: [
        {
          id: 'a',
          label: t('Certifikát a příprava odsávačky', 'Certificate and recovery machine setup'),
        },
        { id: 'b', label: t('Odsátí do servisní lahve', 'Recovery into a service cylinder') },
        { id: 'c', label: t('Zápis do evidence', 'Log in the records') },
        {
          id: 'd',
          label: t('Demontáž a předání k recyklaci', 'Dismantle and hand over for recycling'),
        },
      ],
      correct: ['a', 'b', 'c', 'd'],
    },
  ],
};
